/**
 * React Query hooks for user strategy CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserStrategies,
  getUserStrategy,
  createUserStrategy,
  updateUserStrategy,
  deleteUserStrategy,
  listUserStrategyVersions,
  getStrategyGroupedConfigs,
  getStrategyUsedInPortfolios,
  getStrategyOptimizations,
} from '../api/client';
import type { UserStrategy } from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const userStrategyKeys = {
  all: ['user-strategies'] as const,
  detail: (id: string) => ['user-strategies', id] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all user strategies for the current user.
 */
export function useUserStrategies() {
  return useQuery<UserStrategy[], Error>({
    queryKey: userStrategyKeys.all,
    queryFn: getUserStrategies,
    staleTime: 60_000, // 1 min
  });
}

/**
 * Fetch a single user strategy by ID.
 */
export function useUserStrategy(id: string | null) {
  return useQuery<UserStrategy, Error>({
    queryKey: userStrategyKeys.detail(id ?? ''),
    queryFn: () => getUserStrategy(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * Create a new user strategy.
 */
export function useCreateUserStrategy() {
  const queryClient = useQueryClient();
  return useMutation<
    UserStrategy,
    Error,
    { name: string; description?: string; code: string }
  >({
    mutationFn: createUserStrategy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userStrategyKeys.all });
    },
  });
}

/**
 * Update an existing user strategy.
 */
export function useUpdateUserStrategy() {
  const queryClient = useQueryClient();
  return useMutation<
    UserStrategy,
    Error,
    { id: string; data: { name?: string; description?: string; code?: string } }
  >({
    mutationFn: ({ id, data }) => updateUserStrategy(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: userStrategyKeys.all });
      queryClient.setQueryData(userStrategyKeys.detail(updated.id), updated);
    },
  });
}

/**
 * Delete a user strategy.
 */
export function useDeleteUserStrategy() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteUserStrategy,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: userStrategyKeys.all });
      queryClient.removeQueries({ queryKey: userStrategyKeys.detail(deletedId) });
    },
  });
}

/**
 * Fetch all version snapshots for a user strategy (version picker + timeline).
 */
export function useStrategyVersionsList(id: string | null) {
  return useQuery({
    queryKey: ['user-strategy-versions', id],
    queryFn: () => listUserStrategyVersions(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * Fetch grouped configs (manual + GS groups) for a user strategy.
 */
export function useStrategyGroupedConfigs(strategyId: string | null) {
  return useQuery({
    queryKey: ['strategy-grouped-configs', strategyId],
    queryFn: () => getStrategyGroupedConfigs(strategyId!),
    enabled: !!strategyId,
    staleTime: 30_000,
  });
}

/**
 * Fetch all non-aof1 portfolios that reference a config from this strategy.
 *
 * @param version - When provided, only portfolios whose matching configs are pinned
 *   to this exact strategy_version integer are returned. The query key includes the
 *   version so that switching versions triggers a fresh fetch.
 */
export function useStrategyUsedInPortfolios(strategyId: string | null, version?: number) {
  return useQuery({
    queryKey: ['strategy-used-in-portfolios', strategyId, version],
    queryFn: () => getStrategyUsedInPortfolios(strategyId!, version),
    enabled: !!strategyId,
    staleTime: 60_000,
  });
}

/**
 * Fetch all GS and WF optimization runs owned by this strategy (migration 065).
 * Returns StrategyOptimizationSummary[] ordered by most-recent first.
 */
export function useStrategyOptimizations(strategyId: string | null) {
  return useQuery({
    queryKey: ['strategy-optimizations', strategyId],
    queryFn: () => getStrategyOptimizations(strategyId!),
    enabled: !!strategyId,
    staleTime: 60_000,
  });
}
